import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '@/lib/api'

// ==================== PREFETCH UTILITIES ====================
// Use these to prefetch data on hover for faster perceived performance

// Prefetch Medway data
export function usePrefetchMedway() {
  const queryClient = useQueryClient()

  return useCallback(() => {
    // Prefetch trails
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'medway-trails'],
      queryFn: () => api.integrations.getMedwayTrails(),
      staleTime: 1000 * 60 * 5,
    })

    // Prefetch question stats
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'question-stats'],
      queryFn: () => api.integrations.getQuestionStats(),
      staleTime: 1000 * 60 * 5,
    })

    // Prefetch estudos
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'medway-estudos'],
      queryFn: () => api.integrations.getMedwayEstudos(),
      staleTime: 1000 * 60 * 30,
    })
  }, [queryClient])
}

// Prefetch MedCof data
export function usePrefetchMedcof() {
  const queryClient = useQueryClient()

  return useCallback(() => {
    // Prefetch profile
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'medcof-profile'],
      queryFn: () => api.integrations.getMedcofProfile(),
      staleTime: 1000 * 60 * 30,
    })

    // Prefetch estudos
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'medcof-estudos'],
      queryFn: () => api.integrations.getMedcofEstudos(),
      staleTime: 1000 * 60 * 30,
    })

    // Prefetch simulados
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'medcof-simulados'],
      queryFn: () => api.integrations.getMedcofSimulados(),
      staleTime: 1000 * 60 * 30,
    })
  }, [queryClient])
}

// Prefetch unified data (combines all platforms)
export function usePrefetchUnified() {
  const queryClient = useQueryClient()

  return useCallback(() => {
    // Prefetch unified estudos
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'unified-estudos'],
      queryFn: () => api.integrations.getUnifiedEstudos(),
      staleTime: 1000 * 60 * 5,
    })

    // Prefetch unified simulados
    queryClient.prefetchQuery({
      queryKey: ['integrations', 'unified-simulados'],
      queryFn: () => api.integrations.getUnifiedSimulados(),
      staleTime: 1000 * 60 * 5,
    })
  }, [queryClient])
}

// List all integration platforms
export function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.integrations.list(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get Medway connection status
export function useMedwayStatus() {
  return useQuery({
    queryKey: ['integrations', 'medway-status'],
    queryFn: () => api.integrations.getMedwayStatus(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

// Get Medway trails
export function useMedwayTrails() {
  return useQuery({
    queryKey: ['integrations', 'medway-trails'],
    queryFn: () => api.integrations.getMedwayTrails(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get Medway estudos (aggregated by study_theme)
export function useMedwayEstudos() {
  return useQuery({
    queryKey: ['integrations', 'medway-estudos'],
    queryFn: () => api.integrations.getMedwayEstudos(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medway revisoes
export function useMedwayRevisoes() {
  return useQuery({
    queryKey: ['integrations', 'medway-revisoes'],
    queryFn: () => api.integrations.getMedwayRevisoes(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medway recommended revisions
export function useMedwayRecommendedRevisions(limit?: number) {
  return useQuery({
    queryKey: ['integrations', 'medway-recommended-revisions', limit],
    queryFn: () => api.integrations.getMedwayRecommendedRevisions(limit),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medway simulados (challenges)
export function useMedwaySimulados() {
  return useQuery({
    queryKey: ['integrations', 'medway-simulados'],
    queryFn: () => api.integrations.getMedwaySimulados(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get question statistics
export function useQuestionStats() {
  return useQuery({
    queryKey: ['integrations', 'question-stats'],
    queryFn: () => api.integrations.getQuestionStats(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get primary studies data
export function usePrimaryStudies() {
  return useQuery({
    queryKey: ['integrations', 'primary-studies'],
    queryFn: () => api.integrations.getPrimaryStudies(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get revisions data
export function useRevisions() {
  return useQuery({
    queryKey: ['integrations', 'revisions'],
    queryFn: () => api.integrations.getRevisions(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get recommended revisions
export function useRecommendedRevisions() {
  return useQuery({
    queryKey: ['integrations', 'recommended-revisions'],
    queryFn: () => api.integrations.getRecommendedRevisions(),
    staleTime: 1000 * 60 * 5,
  })
}

// Sync Medway trails
export function useSyncMedway() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (useApi: boolean = true) => api.integrations.syncMedway(useApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Sync all questions
export function useSyncAllQuestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (useApi: boolean = true) => api.integrations.syncAllQuestions(useApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Sync primary studies
export function useSyncPrimaryStudies() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncPrimaryStudies(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'primary-studies'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Sync revisions
export function useSyncRevisions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncRevisions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'revisions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Sync challenges
export function useSyncChallenges() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncChallenges(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'challenges'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medway-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'unified-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'unified'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Generate recommendations
export function useGenerateRecommendations() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.generateRecommendations(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'recommended-revisions'] })
    },
  })
}

// Complete a recommendation
export function useCompleteRecommendation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => api.integrations.completeRecommendation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'recommended-revisions'] })
    },
  })
}

// Save platform credentials
export function useSaveCredentials() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ platform, email, password }: { platform: string; email: string; password: string }) =>
      api.integrations.saveCredentials(platform, email, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Delete platform credentials
export function useDeleteCredentials() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (platform: string) => api.integrations.deleteCredentials(platform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Test platform connection
export function useTestConnection() {
  return useMutation({
    mutationFn: ({ platform, email, password }: { platform: string; email: string; password: string }) =>
      api.integrations.testConnection(platform, email, password),
  })
}

// ==================== MEDCOF HOOKS ====================

// Get Medcof connection status
export function useMedcofStatus() {
  return useQuery({
    queryKey: ['integrations', 'medcof-status'],
    queryFn: () => api.integrations.getMedcofStatus(),
    staleTime: 1000 * 60 * 2,
  })
}

// Get Medcof profile
export function useMedcofProfile() {
  return useQuery({
    queryKey: ['integrations', 'medcof-profile'],
    queryFn: () => api.integrations.getMedcofProfile(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get Medcof preset
export function useMedcofPreset() {
  return useQuery({
    queryKey: ['integrations', 'medcof-preset'],
    queryFn: () => api.integrations.getMedcofPreset(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get Medcof reports
export function useMedcofReports() {
  return useQuery({
    queryKey: ['integrations', 'medcof-reports'],
    queryFn: () => api.integrations.getMedcofReports(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get Medcof report detail
export function useMedcofReport(id: number) {
  return useQuery({
    queryKey: ['integrations', 'medcof-report', id],
    queryFn: () => api.integrations.getMedcofReport(id),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    enabled: !!id,
  })
}

// Get Medcof aggregated stats
export function useMedcofStats() {
  return useQuery({
    queryKey: ['integrations', 'medcof-stats'],
    queryFn: () => api.integrations.getMedcofStats(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medcof daily products
export function useMedcofDailyProducts() {
  return useQuery({
    queryKey: ['integrations', 'medcof-daily-products'],
    queryFn: () => api.integrations.getMedcofDailyProducts(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medcof estudos (primary studies)
export function useMedcofEstudos() {
  return useQuery({
    queryKey: ['integrations', 'medcof-estudos'],
    queryFn: () => api.integrations.getMedcofEstudos(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get Medcof revisoes
export function useMedcofRevisoes() {
  return useQuery({
    queryKey: ['integrations', 'medcof-revisoes'],
    queryFn: () => api.integrations.getMedcofRevisoes(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medcof recommended revisions
export function useMedcofRecommendedRevisions(limit?: number) {
  return useQuery({
    queryKey: ['integrations', 'medcof-recommended-revisions', limit],
    queryFn: () => api.integrations.getMedcofRecommendedRevisions(limit),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Medcof simulados
export function useMedcofSimulados() {
  return useQuery({
    queryKey: ['integrations', 'medcof-simulados'],
    queryFn: () => api.integrations.getMedcofSimulados(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Sync Medcof data
export function useSyncMedcof() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncMedcof(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-status'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-profile'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-preset'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-stats'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-daily-products'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-estudos'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-revisoes'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-recommended-revisions'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Sync a specific Medcof report
export function useSyncMedcofReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reportId: string) => api.integrations.syncMedcofReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-reports'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-stats'] })
    },
  })
}

// ==================== ARISTO HOOKS ====================

// Get Aristo connection status
export function useAristoStatus() {
  return useQuery({
    queryKey: ['integrations', 'aristo-status'],
    queryFn: () => api.integrations.getAristoStatus(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

// Connect Aristo account
export function useConnectAristo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.integrations.connectAristo(email, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-status'] })
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Disconnect Aristo account
export function useDisconnectAristo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.disconnectAristo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-status'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-estudos'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-revisoes'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'unified'] })
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
    },
  })
}

// Sync Aristo data
export function useSyncAristo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncAristo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-status'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-estudos'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-revisoes'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'unified-estudos'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'unified-revisoes'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'unified-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Get Aristo estudos (primary studies)
export function useAristoEstudos() {
  return useQuery({
    queryKey: ['integrations', 'aristo-estudos'],
    queryFn: () => api.integrations.getAristoEstudos(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Aristo revisoes
export function useAristoRevisoes() {
  return useQuery({
    queryKey: ['integrations', 'aristo-revisoes'],
    queryFn: () => api.integrations.getAristoRevisoes(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Aristo simulados
export function useAristoSimulados() {
  return useQuery({
    queryKey: ['integrations', 'aristo-simulados'],
    queryFn: () => api.integrations.getAristoSimulados(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Get Aristo recommended revisions
export function useAristoRecommendedRevisions() {
  return useQuery({
    queryKey: ['integrations', 'aristo-recommended-revisions'],
    queryFn: () => api.integrations.getAristoRecommendedRevisions(),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  })
}

// Sync Aristo simulado details (fetch question data)
export function useSyncAristoSimuladoDetails() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ externalId, examType }: { externalId: string; examType?: string }) =>
      api.integrations.syncAristoSimuladoDetails(externalId, examType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-simulados'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'aristo-recommended-revisions'] })
    },
  })
}

// ==================== UNIFIED HOOKS ====================
// These combine data from all integrated platforms

// Get integration status (which platforms are connected)
export function useIntegrationStatus() {
  return useQuery({
    queryKey: ['integrations', 'status'],
    queryFn: () => api.integrations.getIntegrationStatus(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Get unified estudos from all platforms
export function useUnifiedEstudos() {
  return useQuery({
    queryKey: ['integrations', 'unified-estudos'],
    queryFn: () => api.integrations.getUnifiedEstudos(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get unified revisoes from all platforms
export function useUnifiedRevisoes() {
  return useQuery({
    queryKey: ['integrations', 'unified-revisoes'],
    queryFn: () => api.integrations.getUnifiedRevisoes(),
    staleTime: 1000 * 60 * 5,
  })
}

// Get unified recommended revisions from all platforms
export function useUnifiedRecommendedRevisions(limit?: number) {
  return useQuery({
    queryKey: ['integrations', 'unified-recommended-revisions', limit],
    queryFn: () => api.integrations.getUnifiedRecommendedRevisions(limit),
    staleTime: 1000 * 60 * 5,
  })
}

// Get unified simulados from all platforms
export function useUnifiedSimulados() {
  return useQuery({
    queryKey: ['integrations', 'unified-simulados'],
    queryFn: () => api.integrations.getUnifiedSimulados(),
    staleTime: 1000 * 60 * 5,
  })
}

// ==================== PROVAS NA INTEGRA HOOKS ====================

// Get all institutions for Provas na Integra
export function useProvasInstitutions() {
  return useQuery({
    queryKey: ['integrations', 'provas-institutions'],
    queryFn: () => api.integrations.getProvasInstitutions(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get exams for Provas na Integra with filters
export function useProvasExams(params?: {
  institutionId?: number
  year?: number
  examType?: string
  page?: number
  perPage?: number
}) {
  return useQuery({
    queryKey: ['integrations', 'provas-exams', params],
    queryFn: () => api.integrations.getProvasExams({
      institution_id: params?.institutionId,
      year: params?.year,
      exam_type: params?.examType,
      page: params?.page,
      per_page: params?.perPage,
    }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

// Get exam detail with questions
export function useProvaExamDetail(examId: number | null) {
  return useQuery({
    queryKey: ['integrations', 'prova-exam-detail', examId],
    queryFn: () => api.integrations.getProvaExamDetail(examId!),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    enabled: !!examId,
  })
}

// Sync institutions and exams from Medway
export function useSyncProvas() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncProvas(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'provas-institutions'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'provas-exams'] })
    },
  })
}

// Sync questions for a specific exam
export function useSyncProvaExamQuestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (examId: number) => api.integrations.syncProvaExamQuestions(examId),
    onSuccess: (_data, examId) => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'prova-exam-detail', examId] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'provas-exams'] })
    },
  })
}

// Sync questions for all exams
export function useSyncAllProvaQuestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params?: { limit?: number; skipSynced?: boolean }) =>
      api.integrations.syncAllProvaQuestions(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'provas-exams'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'prova-exam-detail'] })
    },
  })
}

// ==================== MEDCOF PROVAS NA INTEGRA HOOKS ====================

// Get all groups for MedCof Provas na Integra
export function useMedcofProvasGroups() {
  return useQuery({
    queryKey: ['integrations', 'medcof-provas-groups'],
    queryFn: () => api.integrations.getMedcofProvasGroups(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get exams for MedCof Provas na Integra with filters
export function useMedcofProvasExams(params?: {
  groupId?: number
  year?: number
  examType?: string
  institution?: string
  page?: number
  perPage?: number
}) {
  return useQuery({
    queryKey: ['integrations', 'medcof-provas-exams', params],
    queryFn: () => api.integrations.getMedcofProvasExams({
      group_id: params?.groupId,
      year: params?.year,
      exam_type: params?.examType,
      institution: params?.institution,
      page: params?.page,
      per_page: params?.perPage,
    }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

// Get MedCof exam detail with questions
export function useMedcofProvaExamDetail(examId: number | null) {
  return useQuery({
    queryKey: ['integrations', 'medcof-prova-exam-detail', examId],
    queryFn: () => api.integrations.getMedcofProvaExamDetail(examId!),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    enabled: !!examId,
  })
}

// Sync groups and exams from MedCof
export function useSyncMedcofProvas() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.integrations.syncMedcofProvas(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-groups'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-exams'] })
    },
  })
}

// Sync questions for a specific MedCof exam
export function useSyncMedcofProvaExamQuestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (examId: number) => api.integrations.syncMedcofProvaExamQuestions(examId),
    onSuccess: (_data, examId) => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-prova-exam-detail', examId] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-exams'] })
    },
  })
}

// Sync questions for all MedCof exams
export function useSyncAllMedcofProvaQuestions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params?: { limit?: number; skipSynced?: boolean }) =>
      api.integrations.syncAllMedcofProvaQuestions(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-exams'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-prova-exam-detail'] })
    },
  })
}

// Get available user tests for MedCof (tests the user has already started via web)
export function useMedcofProvasAvailableUserTests() {
  return useQuery({
    queryKey: ['integrations', 'medcof-provas-available-user-tests'],
    queryFn: () => api.integrations.getMedcofProvasAvailableUserTests(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
  })
}

// Sync questions from user's existing test instances (the working approach)
export function useSyncMedcofProvasFromUserTests() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params?: { maxPages?: number }) =>
      api.integrations.syncMedcofProvasFromUserTests(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-groups'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-exams'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-prova-exam-detail'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', 'medcof-provas-available-user-tests'] })
    },
  })
}

// ==================== PREVALENCE ANALYSIS HOOKS ====================

// Get all institutions with question counts
export function usePrevalenceInstitutions() {
  return useQuery({
    queryKey: ['prevalence', 'institutions'],
    queryFn: () => api.prevalence.getInstitutions(),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

// Get prevalence data for a specific institution
export function usePrevalenceByInstitution(institutionId: number | null) {
  return useQuery({
    queryKey: ['prevalence', 'by-institution', institutionId],
    queryFn: () => api.prevalence.getByInstitution(institutionId!),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    enabled: !!institutionId,
  })
}

// Get filtered questions by institution and study theme
export function usePrevalenceQuestions(params: {
  institutionId: number | null
  studyTheme?: string
  page?: number
  perPage?: number
}) {
  return useQuery({
    queryKey: ['prevalence', 'questions', params],
    queryFn: () => api.prevalence.getQuestions({
      institution_id: params.institutionId!,
      study_theme: params.studyTheme,
      page: params.page,
      per_page: params.perPage,
    }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    enabled: !!params.institutionId,
  })
}

// Get all unique study themes
export function usePrevalenceStudyThemes() {
  return useQuery({
    queryKey: ['prevalence', 'study-themes'],
    queryFn: () => api.prevalence.getStudyThemes(),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 2, // 2 hours
  })
}

// Get institutions with similar exam content
export function useSimilarInstitutions(institutionId: number | null) {
  return useQuery({
    queryKey: ['prevalence', 'similar-institutions', institutionId],
    queryFn: () => api.prevalence.getSimilarInstitutions(institutionId!),
    enabled: !!institutionId,
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}
